import React, { useState, useEffect } from "react";
import { 
    Card,
    Typography,
    Row,
    Col,
    Descriptions,
    Button,
    Form,
    Input,
    message,
    Space,
    Avatar,
    ColorPicker,
    Tag,
    Select
} from 'antd';
import { 
    EditOutlined,
    SaveOutlined,
    CloseOutlined,
    TagOutlined,
    LinkOutlined,
    BgColorsOutlined,
    CalendarOutlined,
    IdcardOutlined
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useAuth } from '../../hooks/useAuth';
import { useUpdateBrandMutation } from '../../services/nestjs-base-api';
import { IBrand } from '../../services/nestjs-base-api/types';
import { TemplateTypeEnum, TemplateTypeOptions } from '../../enums/TemplateTypeEnum';

const { Title, Text } = Typography;

interface BrandDetailsProps {
    brand: IBrand;
}

const BrandDetails: React.FC<BrandDetailsProps> = ({ brand }) => {
    const intl = useIntl();
    const { user } = useAuth();
    const [isEditing, setIsEditing] = useState(false);
    const [form] = Form.useForm();
    const [primaryColor, setPrimaryColor] = useState<string>(brand.primaryColor || '#4338ca');
    
    const [updateBrand, { isLoading: isUpdating }] = useUpdateBrandMutation();

    // Update form values when entering edit mode
    useEffect(() => {
        if (isEditing) {
            const formValues = {
                name: brand.name,
                logoUrl: brand.logoUrl || '',
                templateType: brand.templateType,
            };
            form.setFieldsValue(formValues);
            setPrimaryColor(brand.primaryColor || '#4338ca');
        }
    }, [isEditing, brand, form]);

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        form.resetFields();
        setPrimaryColor(brand.primaryColor || '#4338ca');
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            
            await updateBrand({
                jwtToken: user!.jwtToken,
                organisationId: user!.organisation.id,
                brandId: brand.id,
                payload: {
                    name: values.name,
                    logoUrl: values.logoUrl || undefined,
                    primaryColor: primaryColor,
                    templateType: values.templateType || undefined,
                },
            }).unwrap();

            message.success(
                intl.formatMessage(
                    { id: 'brands.edit.success' },
                    { name: values.name }
                )
            );
            setIsEditing(false);
        } catch (error: any) {
            if (error?.data?.message) {
                message.error(error.data.message);
            } else {
                message.error(
                    intl.formatMessage({ id: 'brands.edit.error' })
                );
            }
        }
    };

    return (
        <div style={{ padding: '0 0 24px 0' }}>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={4} style={{ margin: 0 }}>
                    {intl.formatMessage({ id: 'brands.details.title' })}
                </Title>
                {!isEditing && (
                    <Button 
                        type="primary" 
                        icon={<EditOutlined />}
                        onClick={handleEdit}
                    >
                        {intl.formatMessage({ id: 'brands.details.editButton' })}
                    </Button>
                )}
            </div>

            {isEditing ? (
                <Card>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSave}
                    >
                        <Row gutter={[24, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={intl.formatMessage({ id: 'brands.form.name' })}
                                    name="name"
                                    rules={[
                                        { 
                                            required: true, 
                                            message: intl.formatMessage({ id: 'brands.form.nameRequired' })
                                        },
                                        { 
                                            min: 2, 
                                            message: intl.formatMessage({ id: 'brands.form.nameMinLength' })
                                        }
                                    ]}
                                >
                                    <Input 
                                        prefix={<TagOutlined />}
                                        placeholder={intl.formatMessage({ id: 'brands.form.namePlaceholder' })}
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={intl.formatMessage({ id: 'brands.form.logoUrl' })}
                                    name="logoUrl"
                                    rules={[
                                        { type: 'url', message: intl.formatMessage({ id: 'brands.form.logoUrlInvalid' }) }
                                    ]}
                                >
                                    <Input 
                                        prefix={<LinkOutlined />}
                                        placeholder={intl.formatMessage({ id: 'brands.form.logoUrlPlaceholder' })}
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Product Type"
                                    name="templateType"
                                >
                                    <Select
                                        placeholder="What type of products does this brand sell?"
                                        size="large"
                                        allowClear
                                        options={TemplateTypeOptions}
                                    />
                                </Form.Item>
                                <div style={{ marginTop: -25, marginBottom: 8 }}>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        This determines which question set will be used for surveys.
                                    </Text>
                                </div>
                            </Col>
                        </Row>
                        
                        <Form.Item
                            label={intl.formatMessage({ id: 'brands.form.primaryColor' })}
                        >
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <ColorPicker
                                    value={primaryColor}
                                    onChange={(color) => setPrimaryColor(color.toHexString())}
                                    showText
                                    size="large"
                                    style={{ width: '200px' }}
                                />
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    {intl.formatMessage({ id: 'brands.form.primaryColorDescription' })}
                                </Text>
                            </Space>
                        </Form.Item>
                        
                        <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
                            <Button 
                                type="primary" 
                                icon={<SaveOutlined />}
                                onClick={handleSave}
                                loading={isUpdating}
                            >
                                {intl.formatMessage({ id: 'brands.edit.submit' })}
                            </Button>
                            <Button 
                                icon={<CloseOutlined />}
                                onClick={handleCancel}
                            >
                                {intl.formatMessage({ id: 'common.cancelButton' })}
                            </Button>
                        </div>
                    </Form>
                </Card>
            ) : (
                <Row gutter={[24, 24]}>
                    <Col xs={24} lg={16}>
                        <Card title={intl.formatMessage({ id: 'brands.details.basicInfo' })}>
                            <Descriptions column={1} size="middle" colon={false}>
                                <Descriptions.Item 
                                    label={
                                        <Space>
                                            <TagOutlined />
                                            <Text strong>{intl.formatMessage({ id: 'brands.details.brandName' })}</Text>
                                        </Space>
                                    }
                                >
                                    <Space>
                                        <Avatar 
                                            size={32}
                                            src={brand.logoUrl}
                                            style={{ 
                                                backgroundColor: brand.primaryColor || '#4338ca',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {!brand.logoUrl && brand.name.charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Text style={{ fontSize: '16px' }}>{brand.name}</Text>
                                    </Space>
                                </Descriptions.Item>
                                
                                <Descriptions.Item 
                                    label={
                                        <Space>
                                            <IdcardOutlined />
                                            <Text strong>{intl.formatMessage({ id: 'brands.details.brandId' })}</Text>
                                        </Space>
                                    }
                                >
                                    <Text code style={{ fontSize: '14px' }}>{brand.hash}</Text>
                                </Descriptions.Item>
                                
                                <Descriptions.Item 
                                    label={
                                        <Space>
                                            <LinkOutlined />
                                            <Text strong>{intl.formatMessage({ id: 'brands.details.logoUrl' })}</Text>
                                        </Space>
                                    }
                                >
                                    {brand.logoUrl ? (
                                        <a href={brand.logoUrl} target="_blank" rel="noopener noreferrer">
                                            <Text ellipsis style={{ maxWidth: '300px' }}>
                                                {brand.logoUrl}
                                            </Text>
                                        </a>
                                    ) : (
                                        <Text type="secondary">{intl.formatMessage({ id: 'brands.details.noLogo' })}</Text>
                                    )}
                                </Descriptions.Item>
                                
                                <Descriptions.Item 
                                    label={
                                        <Space>
                                            <BgColorsOutlined />
                                            <Text strong>{intl.formatMessage({ id: 'brands.details.primaryColor' })}</Text>
                                        </Space>
                                    }
                                >
                                    <Space>
                                        <div 
                                            style={{ 
                                                width: 20, 
                                                height: 20, 
                                                borderRadius: 4,
                                                backgroundColor: brand.primaryColor || '#4338ca',
                                                border: '1px solid #d9d9d9'
                                            }} 
                                        />
                                        <Text code>{brand.primaryColor || '#4338ca'}</Text>
                                    </Space>
                                </Descriptions.Item>

                                <Descriptions.Item 
                                    label={
                                        <Space>
                                            <TagOutlined />
                                            <Text strong>Product Type</Text>
                                        </Space>
                                    }
                                >
                                    {brand.templateType ? (
                                        <Tag color={brand.templateType === TemplateTypeEnum.DURABLE ? 'blue' : 'green'}>
                                            {brand.templateType === TemplateTypeEnum.DURABLE ? 'Durable goods' : 'Perishable goods'}
                                        </Tag>
                                    ) : (
                                        <Text type="secondary">Not set</Text>
                                    )}
                                </Descriptions.Item>
                                
                                <Descriptions.Item 
                                    label={
                                        <Space>
                                            <CalendarOutlined />
                                            <Text strong>{intl.formatMessage({ id: 'brands.details.created' })}</Text>
                                        </Space>
                                    }
                                >
                                    <Text>{new Date(brand.createdAt).toLocaleDateString()}</Text>
                                </Descriptions.Item>
                                
                                <Descriptions.Item 
                                    label={
                                        <Space>
                                            <CalendarOutlined />
                                            <Text strong>{intl.formatMessage({ id: 'brands.details.lastUpdated' })}</Text>
                                        </Space>
                                    }
                                >
                                    <Text>{new Date(brand.updatedAt).toLocaleDateString()}</Text>
                                </Descriptions.Item>
                                
                                <Descriptions.Item 
                                    label={
                                        <Space>
                                            <TagOutlined />
                                            <Text strong>{intl.formatMessage({ id: 'brands.details.status' })}</Text>
                                        </Space>
                                    }
                                >
                                    <Tag color="success">{intl.formatMessage({ id: 'brands.details.statusActive' })}</Tag>
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    </Col>
                    
                    <Col xs={24} lg={8}>
                        <Card title={intl.formatMessage({ id: 'brands.details.quickActions' })}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Button 
                                    type="default" 
                                    icon={<EditOutlined />}
                                    onClick={handleEdit}
                                    block
                                >
                                    {intl.formatMessage({ id: 'brands.details.editButton' })}
                                </Button>
                                <Text type="secondary" style={{ fontSize: 12, textAlign: 'center', display: 'block' }}>
                                    {intl.formatMessage({ id: 'brands.details.editDescription' })}
                                </Text>
                            </Space>
                        </Card>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default BrandDetails;